import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";

import { Button, Grid, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { calculateDurationInMins } from "../../services/misc.service";
import { LoadingOverlay } from "../../components/Navigation/LoadingOverlay";
import { RiderContext } from "../../context";
import { StringUtils } from "../../util";
import Table from "../../components/Layout/Table";
import ReservationTableButton from "./subcomponents/ReservationTableButton";

const { formatNumber } = StringUtils;

const useStyles = makeStyles((theme) => ({
  noPadding: {
    padding: 0,
  },
  buttonsWrapper: {
    display: "flex",
    width: "90%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  borderLeft: {
    borderLeft: `1px solid ${theme.palette.background.selected}`,
  },
  clickableVehicle: {
    backgroundColor: theme.palette.background.overlay,
    marginLeft: theme.spacing(1),
    padding: 0,
  },
}));

const Reservations = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const location = useLocation();
  const { riderId } = useParams();
  const { t } = useTranslation();
  const { fetchReservations, reservations, endReservation, wallet } = useContext(RiderContext);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const rowsPerPageOptions = [15, 30];

  useEffect(() => {
    handleFetchReservationsWithLoading();
    return () => {
      fetchReservations(null);
    };
  }, [location, riderId, rowsPerPage]);

  const handleFetchReservations = async () => {
    try {
      await fetchReservations(riderId, {
        page_size: rowsPerPage,
        page: location.state?.page || 1,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleFetchReservationsWithLoading = async () => {
    setLoading(true);
    await handleFetchReservations();
    setLoading(false);
  };

  if (loading) {
    return <LoadingOverlay />;
  }
  if (!reservations.data?.length) {
    return <Typography variant="body1">{t("Riders.Reservations.RequestError")}</Typography>;
  }

  const currentTimezone = DateTime.fromISO(new Date().toISOString()).offsetNameShort;

  const formatDistance = (n) => {
    if (Number(n) === n) {
      return Number.isInteger(n) ? n : n.toFixed(2);
    }
    return "-";
  };

  const handlePageChange = async (page) => {
    setLoading(true);
    try {
      await fetchReservations(riderId, {
        page_size: rowsPerPage,
        page: page + 1,
      });
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const shouldGroupRow = (row, idx) => {
    const nextRow = reservations?.data[idx + 1];
    return row?.group_reservation && row?.group_reservation === nextRow?.group_reservation;
  };

  const tableColumns = [
    {
      canonicalName: "startDate",
      name: `${t("Riders.Reservations.Date")}, ${currentTimezone}`,
      renderCellContent: (row) => (row.start ? moment(row.start).format("MMM D YYYY") : ""),
    },
    {
      canonicalName: "reservationTime",
      name: t("Riders.Reservations.ReservationTime"),
      tableCellProps: { className: classes.borderLeft },
      renderCellContent: (row) =>
        `${row.start ? moment(row.start).format("HH:mm") : ""} / ${
          row.stop ? moment(row.stop).format("HH:mm") : ""
        }`,
    },
    {
      canonicalName: "tripTime",
      name: t("Riders.Reservations.TripTime"),
      renderCellContent: (row) =>
        row.trip?.id
          ? `${row.trip?.start ? moment(row.trip?.start).format("HH:mm") : ""} / ${
              row.trip?.stop ? moment(row.trip?.stop).format("HH:mm") : ""
            }`
          : "",
    },
    {
      canonicalName: "billableTime",
      name: t("Riders.Reservations.BillableTime"),
      renderCellContent: (row) =>
        row.trip?.id ? calculateDurationInMins(row.trip?.start, row.trip?.stop) : "",
    },
    {
      canonicalName: "tripDistance",
      name: t("Riders.Reservations.Distance"),
      tableCellProps: { className: classes.borderLeft },
      renderCellContent: (row) => (row.trip?.id ? `${formatDistance(row.trip?.distance)} km` : ""),
    },
    {
      canonicalName: "fare",
      name: t("Riders.Reservations.Fare"),
      renderCellContent: (row) =>
        row.trip?.stop &&
        !row.group_reservation &&
        formatNumber(
          row.receipt?.total_amount * (row.receipt?.exchange_rate?.rate?.rate || 1),
          wallet?.currency,
        ),
    },
    {
      canonicalName: "vehicle",
      name: t("Riders.Reservations.Vehicle"),
      tableCellProps: { padding: "none", className: classes.borderLeft },
      renderCellContent: (row) => (
        <div className={classes.buttonsWrapper}>
          {row?.fleets?.length ? (
            <Link to={`/fleets/${row.fleets[0]}/vehicles/${row.vehicleId}`}>
              <Button className={classes.clickableVehicle}>
                {row.vehicleCode ? row.vehicleCode : ""}
              </Button>
            </Link>
          ) : (
            <Button disabled className={classes.clickableVehicle}>
              {row.vehicleCode ? row.vehicleCode : ""}
            </Button>
          )}
          <ReservationTableButton
            reservation={row}
            riderId={riderId}
            endReservation={endReservation}
            handleFetchReservationsWithLoading={handleFetchReservationsWithLoading}
            handleFetchReservations={handleFetchReservations}
          />
          <Link to={`/riders/${riderId}/reservations/${row.id}`}>
            <Button>{t("Riders.Reservations.Buttons.View")}</Button>
          </Link>
        </div>
      ),
    },
  ];

  return (
    <Grid item xs={12} className={classes.noPadding}>
      <Table
        muiTableProps={{ stickyHeader: true }}
        shouldGroupRow={shouldGroupRow}
        rows={reservations?.data}
        columns={tableColumns}
        paginationProps={{
          rowsPerPage,
          rowsPerPageOptions,
          pageNumber: reservations?.currentPage ? reservations.currentPage - 1 : 0,
          totalRowsCount: reservations?.count ?? 0,
          setPageNumber: handlePageChange,
          setRowsPerPage,
        }}
      />
    </Grid>
  );
};

export default Reservations;
